import {FC, memo, useCallback, useEffect, useMemo, useState} from 'react';
import {Popover} from '@chakra-ui/react';

import {parseValue, toValueString} from './DateTimePicker.utils';
import {Calendar, PickerTrigger, TimeRow} from './components';

interface IProps {
  value?: string;
  disabled?: boolean;
  min?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
}

const DateTimePickerFC: FC<IProps> = ({value, disabled, min, placeholder, onChange}) => {
  const [isOpen, setIsOpen] = useState(false);

  const selected = useMemo(() => parseValue(value), [value]);
  const minDate = useMemo(() => parseValue(min), [min]);

  const [view, setView] = useState(() => selected ?? new Date());

  // Re-focus the calendar on the selected month whenever the popup is (re)opened.
  useEffect(() => {
    if (isOpen) setView(selected ?? new Date());
  }, [isOpen, selected]);

  const handleClose = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    document.addEventListener('scroll', handleClose, true);
    return () => document.removeEventListener('scroll', handleClose, true);
  }, [handleClose]);

  const viewYear = view.getFullYear();
  const viewMonth = view.getMonth();

  const commit = (date: Date) => onChange?.(toValueString(date));

  const handleDayClick = (day: Date) => {
    const base = selected ?? new Date();
    const next = new Date(day);
    next.setHours(base.getHours(), base.getMinutes(), 0, 0);
    commit(next);
  };

  const handleTimeChange = (part: 'hours' | 'minutes', raw: string) => {
    const num = Number(raw);
    if (Number.isNaN(num)) return;
    const next = new Date(selected ?? new Date());
    if (part === 'hours') next.setHours(Math.min(23, Math.max(0, num)));
    else next.setMinutes(Math.min(59, Math.max(0, num)));
    next.setSeconds(0, 0);
    commit(next);
  };

  const goMonth = (delta: number) => setView(new Date(viewYear, viewMonth + delta, 1));

  const displayText = selected
    ? toValueString(selected).replace('T', ' ')
    : (placeholder ?? 'Select date and time');

  return (
    <Popover.Root
      open={isOpen}
      closeOnEscape
      closeOnInteractOutside
      positioning={{placement: 'bottom-start', strategy: 'fixed'}}
      onOpenChange={(e) => !disabled && setIsOpen(e.open)}
    >
      <Popover.Trigger asChild>
        <PickerTrigger
          displayText={displayText}
          filled={Boolean(selected)}
          isOpen={isOpen}
          disabled={disabled}
        />
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content w={'fit-content'} borderRadius={'12px'}>
          <Popover.Body
            p={'12px'}
            background={'{colors.white}'}
            border={'thin solid'}
            borderColor={'{colors.borderDefault}'}
            borderRadius={'12px'}
            boxShadow={'{shadows.modal}'}
          >
            <Calendar
              viewYear={viewYear}
              viewMonth={viewMonth}
              selected={selected}
              minDate={minDate}
              onDayClick={handleDayClick}
              onPrevMonth={() => goMonth(-1)}
              onNextMonth={() => goMonth(1)}
            />
            <TimeRow selected={selected} onChange={handleTimeChange} />
          </Popover.Body>
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
};

export const DateTimePicker = memo(DateTimePickerFC);
